import { CopyButton } from '@/components/primitives/copy-button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { middleTruncate } from '@/lib/format';
import { cn } from '@/lib/utils';

/** Middle truncation keeps 8 + 5 characters: "SAOO11B2…60BE4" (§7.1). */
const HEAD = 8;
const TAIL = 5;
const DEFAULT_END_MAX = 24;

export interface ReferenceProps {
  value: string;
  /** A short muted prefix such as "Ref" or "ID" ("ID 1a2b3c4d"). */
  label?: string;
  /** Default "middle". */
  truncate?: 'none' | 'middle' | 'end';
  /** Longest value shown in full before truncating. */
  maxChars?: number;
  /** Shows the copy button (on hover and focus, always on touch). Default true. */
  copy?: boolean;
  /** 12 px (default) or 13 px mono. */
  size?: 'xs' | 'sm';
  className?: string;
}

/** The text a reference shows, after truncation. */
function referenceText(
  value: string,
  truncate: ReferenceProps['truncate'] = 'middle',
  maxChars?: number,
): string {
  if (truncate === 'none') return value;
  if (truncate === 'end') {
    const max = Math.max(4, maxChars ?? DEFAULT_END_MAX);
    return value.length > max ? `${value.slice(0, max - 1)}…` : value;
  }
  if (maxChars !== undefined && value.length <= maxChars) return value;
  return middleTruncate(value, HEAD, TAIL);
}

/**
 * A reference, id or code in IBM Plex Mono (§7.1), never a name or money. A
 * truncated value can be focused to show it in full, and screen readers hear
 * the full value. The copy button copies the full value.
 */
export function Reference({
  value,
  label,
  truncate = 'middle',
  maxChars,
  copy = true,
  size = 'xs',
  className,
}: Readonly<ReferenceProps>) {
  const shown = referenceText(value, truncate, maxChars);
  const isTruncated = shown !== value;
  const mono = cn(
    'font-mono tracking-normal text-foreground',
    size === 'sm' ? 'text-sm' : 'text-xs',
  );

  return (
    <span
      data-slot="reference"
      className={cn(
        'group/ref inline-flex max-w-full items-center gap-1 align-middle whitespace-nowrap',
        className,
      )}
    >
      {label ? (
        <span className="text-xs text-muted-foreground">{label}</span>
      ) : null}
      {isTruncated ? (
        <Tooltip>
          <TooltipTrigger
            render={
              // A button so the full value is reachable by focus, not only hover (§6.12).
              <button
                type="button"
                className={cn(mono, 'cursor-default rounded-xs')}
              />
            }
          >
            <span aria-hidden="true">{shown}</span>
            <span className="sr-only">{value}</span>
          </TooltipTrigger>
          <TooltipContent className="font-mono">{value}</TooltipContent>
        </Tooltip>
      ) : (
        <span className={mono}>{value}</span>
      )}
      {copy ? (
        <CopyButton
          value={value}
          className="opacity-0 group-focus-within/ref:opacity-100 group-hover/ref:opacity-100 pointer-coarse:opacity-100"
        />
      ) : null}
    </span>
  );
}
